import { useState, useCallback } from 'react';
import type { FlowNode, FlowEdge, FlowNodeData } from '../types/flowchart.types';

interface InlineEdgeEditState {
  edgeId: string;
  value: string;
  pos: { x: number; y: number };
}

interface UseInlineEditOptions {
  nodes: FlowNode[];
  edges: FlowEdge[];
  pushState: (state: { nodes: FlowNode[]; edges: FlowEdge[] }) => void;
  setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>;
  setEdges: React.Dispatch<React.SetStateAction<FlowEdge[]>>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function useInlineEdit({
  nodes,
  edges,
  pushState,
  setNodes,
  setEdges,
  containerRef,
}: UseInlineEditOptions) {
  const [inlineEdgeEdit, setInlineEdgeEdit] = useState<InlineEdgeEditState | null>(null);

  const handleNodeDoubleClick = useCallback(
    (node: FlowNode) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id ? { ...n, data: { ...n.data, editing: true } as FlowNodeData } : n,
        ),
      );
    },
    [setNodes],
  );

  const handleEdgeDoubleClick = useCallback(
    (edge: FlowEdge) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      setInlineEdgeEdit({
        edgeId: edge.id,
        value: (edge.label as string) ?? '',
        pos: { x: rect.width / 2 - 60, y: rect.height / 2 - 15 },
      });
    },
    [containerRef],
  );

  const handleInlineEdgeEditSave = useCallback(() => {
    if (!inlineEdgeEdit) return;
    pushState({ nodes, edges });
    setEdges((eds) =>
      eds.map((e) =>
        e.id === inlineEdgeEdit.edgeId ? { ...e, label: inlineEdgeEdit.value || undefined } : e,
      ),
    );
    setInlineEdgeEdit(null);
  }, [inlineEdgeEdit, pushState, nodes, edges, setEdges]);

  return {
    inlineEdgeEdit,
    setInlineEdgeEdit,
    handleNodeDoubleClick,
    handleEdgeDoubleClick,
    handleInlineEdgeEditSave,
  };
}
